/**
 * TrialStatusBanner
 *
 * App-wide notice (same surface rules as GlobalLowBalanceBanner) that keeps
 * trial users oriented: how many days their trial has left and how much
 * credit remains — denominated in PRO Work Units (pr0) with the logo-zero
 * mark (2026-08-14 owner directive; supersedes the dollar copy). Trial users
 * bypass the low-balance banner entirely (entitlementBypass), so this is
 * their one persistent status surface.
 *
 * Rendering: registers with the surrounding GlobalBannerCenter (the ONE
 * global top strip; rotates when several notices are eligible) and renders
 * nothing itself. Standalone (no BannerBusContext above), it falls back to
 * rendering its legacy strip directly.
 *
 * States:
 *   - Active, comfortable  → calm informational notice. Dismissible, but the
 *     dismissal expires at 5:00 AM local the next morning (bannerDismissal
 *     daily policy) — the banner returns every day. A trial whose plan starts
 *     when it ends says so: "then $3,750/mo on visa •••• 4242".
 *   - Expiring soon (≤3d) or credit used up → warn, not dismissible. A trial
 *     with its plan set says when it starts (manage it on the trial page);
 *     one whose plan was turned off, or with no card at all, is pointed to
 *     where it can keep Prest0n (task #59: hiring is self-serve — no "point of
 *     contact" ever activates an account).
 *   - Waiting for its card (2026-10-20, docs/PLAN-trial-card-on-file-
 *     20261020.md) → warn, not dismissible: work starts when the card is in.
 *   - A declined first month → critical, not dismissible: pay it on the trial
 *     page.
 *   - Expired → critical, not dismissible: hire on the subscription page and
 *     everything picks up where it was.
 * A button appears only for a person who can act on it: the trial page's
 * buttons are for the workspace's owner and billing managers (no dead-end
 * button for anyone else, the low-balance band's rule).
 */

import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { mediaMax } from '../../styles/breakpoints';
import { useBannerSurface } from '../common/bannerSurface';
import { useWorkspaceTrialStatus } from '../../hooks/useTrialStatus';
import {
  BannerBusContext,
  useRegisterBanner,
  type BannerTone,
} from '../common/GlobalBannerCenter';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from '../common/bannerDismissal';
import Pr0Unit from '../common/Pr0Unit';
import { PR0_PIECE, type BannerNotice, type BannerWords } from '../common/bannerNotice';
import { usdToWu, formatWu, WU_PRICING } from '../../config/workUnits';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';

/**
 * v2 (2026-08-14): daily-expiring localStorage dismissal (5 AM policy).
 * The old `prest0n_trialbanner_dismissed` sessionStorage flag is retired.
 */
const DISMISS_KEY = 'prest0n_trialbanner_dismissed_v2';

type Tone = BannerTone;

/* Legacy standalone-fallback palette (pre-center strip). The bus path uses
   the GlobalBannerCenter cream/gold/ink tones instead. */
const TONE_STYLES: Record<Tone, { bg: string; fg: string; border: string; btnBg: string }> = {
  info: { bg: '#E8F0FE', fg: '#1E3A5F', border: 'rgba(30,58,95,0.18)', btnBg: '#1d4ed8' },
  warn: { bg: '#fde68a', fg: '#78350f', border: 'rgba(120,53,15,0.2)', btnBg: '#b45309' },
  critical: { bg: '#fecaca', fg: '#7f1d1d', border: 'rgba(153,27,27,0.25)', btnBg: '#dc2626' },
};

/**
 * In normal document flow (rendered directly below the app Header in
 * App.tsx) — the strip occupies its own band and pushes page content down
 * instead of overlaying the header (owner report 2026-07-31).
 */
const Strip = styled.div<{ $tone: Tone }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => TONE_STYLES[p.$tone].fg};
  background: ${(p) => TONE_STYLES[p.$tone].bg};
  border-bottom: 1px solid ${(p) => TONE_STYLES[p.$tone].border};
`;

const Message = styled.span`
  max-width: 80vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* The 640px block that used to sit here unwrapped this to full text on
     phones — the ONE source in the band that opted OUT of ellipsis there, and
     the direct cause of the ~109px trial strip in the owner's 2026-09-12
     screenshot. 640 also matched nothing else in the repo. The band now owns
     the narrow tier (mediaMax.tablet) and this source supplies shortContent
     instead of a longer line. */
  ${mediaMax.tablet} {
    white-space: normal;
    line-height: 1.3;
  }
`;

const ContactButton = styled.a<{ $tone: Tone }>`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${(p) => TONE_STYLES[p.$tone].btnBg};
  border: none;
  text-decoration: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

const DismissButton = styled.button`
  flex: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  padding: 2px 4px;
  line-height: 0;
  &:hover { opacity: 1; }
  svg { width: 16px; height: 16px; }
`;

export const TrialStatusBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  // Keyed to the ACTIVE WORKSPACE (owner report 2026-08-17): the banner shows
  // the same trial numbers as the wallet page the user is in — the acting
  // account's trial state + paying-wallet balance, never the signed-in
  // member's native account. In a non-trial workspace the banner stays hidden
  // even when the member's own account is on a trial.
  const trial = useWorkspaceTrialStatus();
  const onAppSurface = useBannerSurface();
  const vis = useUsageVisibility();

  // Daily dismissal (calm state only): the X holds until 5:00 AM local the
  // next morning, then the banner returns (owner directive 2026-08-14).
  const [dismissedAtIso, setDismissedAtIso] = useState<string | null>(() =>
    readDailyDismissal(DISMISS_KEY),
  );
  const dismissed = isDismissalActive(dismissedAtIso);

  const eligible = !trial.isLoading && trial.isTrialUser && onAppSurface;
  // The trial's conversion (2026-10-20): its plan set to start, turned off, waiting for its card, or declined.
  const conversion = trial.conversion;
  const awaitingCard = conversion?.required === true && conversion.state === 'awaiting_card';
  const declined = conversion?.state === 'declined';
  const planSet = conversion?.state === 'armed' || conversion?.state === 'charging';
  const planOff = conversion?.state === 'off' || conversion?.state === 'needs_consent';
  const creditsExhausted = trial.isActive && trial.creditRemainingCents <= 0;
  const urgent = awaitingCard || declined || trial.isExpired || trial.isExpiringSoon || creditsExhausted;

  // Calm state hides only while a same-morning dismissal holds; urgent
  // states always show.
  const visible = eligible && (urgent || !dismissed);

  const tone: Tone = declined || (trial.isExpired && !awaitingCard && !planSet) ? 'critical' : urgent ? 'warn' : 'info';
  // The plan after the trial, as the trial page and every reminder say it.
  const monthly = `$${WU_PRICING.monthly.monthlyUsd.toLocaleString('en-US')}/mo`;
  const onCard = conversion?.card ? ` on ${conversion.card.brand.toLowerCase()} •••• ${conversion.card.last4}` : '';
  const planStartsOn = conversion?.chargeAtMs
    ? new Date(conversion.chargeAtMs).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : trial.endsOn;
  // ONE number everywhere (task #76): the banner prints the workspace's
  // actual wallet pr0 — identical to the wallet page — falling back to the
  // standard-rate conversion only until the wallet snapshot lands.
  const wuRemaining = trial.liveWalletWu !== null
    ? formatWu(trial.liveWalletWu)
    : formatWu(usdToWu(trial.creditRemainingCents / 100));
  const days = trial.daysRemaining;
  const daysText = days !== null ? `${days} day${days === 1 ? '' : 's'}` : null;

  // Copy is lowercase by convention (official prest0 style — matches wallet
  // + redemption surfaces); dates keep their natural capitalization. Amounts
  // speak pr0 with the logo-zero mark — never dollars (2026-08-14).
  // The words are data (../common/bannerNotice.ts), one place for both designs'
  // bands: the pr0 unit is a piece of them, drawn here as <Pr0Unit /> and in the
  // new design as the kit's.
  let message: BannerWords;
  if (awaitingCard) {
    message = 'your free trial starts when a card is added — $0 today, and work starts right away.';
  } else if (declined) {
    message = 'your card was declined for your first month, so work is paused — pay it on the trial page to keep your plan.';
  } else if (planSet && (trial.isExpired || trial.isExpiringSoon)) {
    message = `your trial ends${planStartsOn ? ` ${planStartsOn}` : ' soon'}, and your plan starts then: ${monthly}${onCard}. manage it on the trial page.`;
  } else if (trial.isExpired) {
    message = 'your trial has ended. hire your Prest0n on the subscription page, and everything picks up where you left it.';
  } else if (creditsExhausted) {
    message = `you’ve used all of your trial credit${daysText ? ` (trial ends in ${daysText})` : ''}. trial credits can’t be topped up — hire your Prest0n on the subscription page to keep going.`;
  } else if (trial.isExpiringSoon) {
    message = [
      `your trial ends in ${daysText}${trial.endsOn ? ` (${trial.endsOn})` : ''} — ${wuRemaining} `,
      PR0_PIECE,
      planOff
        ? ' remaining. your plan won’t start, so work pauses after it — start it on the trial page.'
        : ' remaining. to keep going after that, hire your Prest0n on the subscription page.',
    ];
  } else {
    // Calm state. No top-up link on purpose: trials can't top up.
    message = [
      `free trial — ${wuRemaining} `,
      PR0_PIECE,
      ` remaining${daysText ? ` · ends in ${daysText}` : ''}${trial.endsOn ? ` on ${trial.endsOn}` : ''}${planSet ? ` · then ${monthly}${onCard}` : ''}`,
    ];
  }

  /**
   * The phone line (2026-09-13). One line each, and each still TRUE — the
   * clause that gets cut is the elaboration, never the fact or the action.
   * Tapping the strip reveals the full text above.
   *
   * The two urgent states state a contractual fact ("trial credits can't be
   * topped up") and are the ones worth a human read: what survives here is
   * that the credit is gone and that a person activates the account, which is
   * everything the reader has to act on. The elaboration is one tap away.
   */
  let shortMessage: BannerWords;
  if (awaitingCard) {
    shortMessage = 'add a card to start your trial — $0 today.';
  } else if (declined) {
    shortMessage = 'card declined — work is paused.';
  } else if (planSet && (trial.isExpired || trial.isExpiringSoon)) {
    shortMessage = `plan starts${planStartsOn ? ` ${planStartsOn}` : ' soon'} · ${monthly}`;
  } else if (trial.isExpired) {
    shortMessage = 'trial ended — hire your Prest0n to keep going.';
  } else if (creditsExhausted) {
    shortMessage = `trial credit used up${daysText ? ` · ${daysText} left` : ''}.`;
  } else if (trial.isExpiringSoon) {
    shortMessage = [`trial ends in ${daysText} · ${wuRemaining} `, PR0_PIECE, ' left'];
  } else {
    shortMessage = [`free trial · ${wuRemaining} `, PR0_PIECE, ` left${daysText ? ` · ${daysText}` : ''}`];
  }

  const handleDismiss = () => {
    const now = new Date();
    writeDailyDismissal(DISMISS_KEY, now);
    setDismissedAtIso(now.toISOString());
  };

  // The one action, where the reader can take it: the trial page's (add a card, pay, manage, start the plan) for the
  // workspace's owner and billing managers; hiring for an ended or used-up trial. The band caps a CTA at 42% of a
  // 390px row before ellipsising it, so each has a short label.
  const onTrialPage = awaitingCard || declined || ((planSet || planOff) && (trial.isExpired || trial.isExpiringSoon));
  const action = !urgent
    ? null
    : awaitingCard
      ? { label: 'add a card', shortLabel: 'add card', href: '/subscription/trial' }
      : declined
        ? { label: 'pay now', shortLabel: 'pay', href: '/subscription/trial' }
        : planSet && (trial.isExpired || trial.isExpiringSoon)
          ? { label: 'manage', shortLabel: 'manage', href: '/subscription/trial' }
          : planOff && trial.isExpiringSoon
            ? { label: 'start my plan', shortLabel: 'start plan', href: '/subscription/trial' }
            : { label: 'hire your Prest0n', shortLabel: 'hire', href: '/subscription' };
  // Billing is the owner's and billing managers' to act on: anyone else reads the line without a dead-end button
  // (the server tells them who to ask when they ask for work).
  const shownAction = action && (!onTrialPage || vis.loading || vis.canManageBilling) ? action : null;
  const renderContent = (body: BannerWords, label: string | null) => (
    <>
      <Message>
        {typeof body === 'string'
          ? body
          : body.map((piece, i) => (typeof piece === 'string' ? piece : <Pr0Unit key={i} />))}
      </Message>
      {shownAction && label && (
        <ContactButton $tone={tone} href={shownAction.href}>
          {label}
        </ContactButton>
      )}
    </>
  );
  const content = renderContent(message, shownAction?.label ?? null);
  const shortContent = renderContent(shortMessage, shownAction?.shortLabel ?? null);
  const notice: BannerNotice = {
    words: message,
    shortWords: shortMessage,
    action: shownAction ?? undefined,
  };

  useRegisterBanner(
    visible
      ? {
          id: 'trial-status',
          tone,
          notice,
          content,
          shortContent,
          dismissible: !urgent,
          onDismiss: !urgent ? handleDismiss : undefined,
        }
      : null,
  );

  // Inside a GlobalBannerCenter the center owns the strip.
  if (bus) return null;

  // ── Legacy standalone fallback (no center above) ──────────────────────────
  if (!visible) return null;

  return (
    <Strip $tone={tone} role="status" aria-live="polite" data-testid="trial-status-banner">
      {content}
      {!urgent && (
        <DismissButton type="button" onClick={handleDismiss} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DismissButton>
      )}
    </Strip>
  );
};

export default TrialStatusBanner;
